import { createSelector } from 'reselect';

import { selectIsWebSDK, selectTargetPlatform } from '@pressreader/appcore';
import { selectGetConfigValue } from '@pressreader/config';
import { TargetPlatform } from '@pressreader/types';
import { selectIsMobileDevice } from '@pressreader/ui-common';
import { isMobileScreen } from '@pressreader/utils';

export type RadioEnabledActive = {
    radioEnabled: boolean;
    radioActive: boolean;
};

/**
 * Radio TTS feature switched on by config/platform — regardless of whether the dedicated Listen
 * (radio) view is reachable. In-place TTS surfaces (e.g. the inline article audio player) follow
 * this selector so they keep working in the Web SDK, which strips the Radio view; "Listen"
 * affordances that navigate to that view must use `selectRadioEnabled` instead.
 */
export const selectRadioFeatureEnabled = createSelector(
    selectGetConfigValue,
    selectTargetPlatform,
    selectIsMobileDevice,
    (getConfigValue, targetPlatform, isMobileDevice) => {
        const allowForPlatform = targetPlatform !== TargetPlatform.Voyager;

        const { enable: enabledFromRadioConfig } = getConfigValue<{ enable: boolean }>('radio', {
            enable: true,
        });

        const enabledFromUIConfig = getConfigValue<boolean>('UI.Listen.enable', true);

        const isMobileView = isMobileDevice && isMobileScreen();
        const showListenOnMobile = isMobileView
            ? getConfigValue<boolean>('features.views.article.optionsTool.showListenOnMobile.enabled', true)
            : true;

        return allowForPlatform && enabledFromRadioConfig && enabledFromUIConfig && showListenOnMobile;
    },
);

/**
 * Whether radio is enabled AND the dedicated Listen (radio) view can be navigated to. The Web SDK
 * bundle does not register the Radio view, so every "Listen" affordance gated by this selector
 * hides there. For the radio TTS feature itself use `selectRadioFeatureEnabled`.
 */
export const selectRadioEnabled = createSelector(
    selectRadioFeatureEnabled,
    selectIsWebSDK,
    (radioFeatureEnabled, isWebSDK) => radioFeatureEnabled && !isWebSDK,
);
